import { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { orderEndingWriteConfig } from '@/common/api/handled-conflict';
import {
  ComplianceInfo,
  CreateOrdersBody,
  DocumentUploadResponse,
  ItemContraChargeRequest,
  Order,
  OrderClaim,
  OrderDelivery,
  OrderStatusUpdateBody,
  ItemVariationReviewBody,
  OrderTotalParams,
  OrderTotals,
  ShareOrder,
} from '@/types/api/orders.types';
import {
  MetaWithoutPagination,
  PaginatedResponse,
  Response as ApiResponse,
  type RequestConfig,
} from '@/types/api/response.types';
import { PaymentRequest } from '@/types/api/organisations.types';

const resource = 'orders';

export const uploadOrderDocuments = async (
  id: string,
  data: FormData,
  config?: Partial<RequestConfig>,
): Promise<AxiosResponse<ApiResponse<DocumentUploadResponse>>> => {
  return await axiosRequest().post(`${resource}/${id}/documents`, data, config);
};

export const deleteOrderDocument = async (
  id: string,
  docId: string,
  config?: Partial<RequestConfig>,
): Promise<AxiosResponse<MetaWithoutPagination>> => {
  return await axiosRequest().delete(`${resource}/${id}/documents/${docId}`, config);
};

export const createOrders = async (
  data: CreateOrdersBody,
): Promise<AxiosResponse<ApiResponse<OrderClaim[]>>> => {
  return axiosRequest().post(`${resource}`, data);
};

export const getOrderStatuses = async (params?: {
  orderType: string;
}): Promise<AxiosResponse<ApiResponse<string[]>>> => {
  return axiosRequest().get(`${resource}/statuses`, { params });
};

export const getOrderById = async (id: string): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().get(`${resource}/${id}`);
};

export const updateOrderStatus = async (
  id: string,
  data: OrderStatusUpdateBody,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  const config = data.status === 'Cancelled' ? orderEndingWriteConfig : {};
  return axiosRequest().patch(`${resource}/${id}/status`, data, config);
};

export const updateOrderVariationStatus = async (
  id: string,
  itemId: string,
  data: ItemVariationReviewBody,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().patch(`${resource}/${id}/items/${itemId}/variations/status`, data);
};

export const getOrderPaymentRequestsById = async (
  orgId: string,
  orderId: string,
): Promise<AxiosResponse<PaginatedResponse<PaymentRequest[]>>> => {
  return axiosRequest().get(`organisations/${orgId}/payment-requests`, { params: { orderId } });
};

/**
 * Context: the same read, one page at a time, asked in the background to learn whether a payment
 * awaiting its proof holds the order (D-30). Nobody asked for it, so it neither turns on the page's
 * loader nor says a failure: a failed read leaves the action to the API, which refuses a held order
 * itself. The `signal` lets the caller cancel a read for an order no longer asked about.
 */
const backgroundReadConfig: Partial<RequestConfig> = {
  skipErrorFeedback: true,
  skipGlobalLoader: true,
};

export const getOrderPaymentRequestsInBackground = async (
  orgId: string,
  orderId: string,
  signal: AbortSignal,
  pageNo: number,
): Promise<AxiosResponse<PaginatedResponse<PaymentRequest[]>>> => {
  const config: Partial<RequestConfig> = {
    ...backgroundReadConfig,
    params: { orderId, pageNo },
    signal,
  };
  return axiosRequest().get(`organisations/${orgId}/payment-requests`, config);
};

export const logOrderDelivery = async (
  id: string,
  data: OrderDelivery[],
  version: number,
): Promise<AxiosResponse<ApiResponse<Order>>> => {
  return axiosRequest().post(`${resource}/${id}/deliveries`, { itemDeliveries: data, version });
};

export const getOrderTotals = async (
  params: OrderTotalParams,
): Promise<AxiosResponse<ApiResponse<OrderTotals>>> => {
  return axiosRequest().get(`organisations/${params.orgId}/orders/totals`, { params: params });
};

export const toggleOrderPlaced = async (
  id: string,
  ordered: boolean,
  orderVersion: number,
): Promise<AxiosResponse<ApiResponse<Order>>> => {
  return axiosRequest().patch(`${resource}/${id}/ordered`, {
    ordered: ordered,
    version: orderVersion,
  });
};

export const exportOrder = (id: string): Promise<AxiosResponse<Blob>> => {
  return axiosRequest().get(`${resource}/${id}/export`, { responseType: 'blob' });
};

export const shareOrder = (
  id: string,
  data: ShareOrder,
): Promise<AxiosResponse<MetaWithoutPagination>> => {
  return axiosRequest().post(`${resource}/${id}/share`, data);
};

export const getOrderDocs = async (
  orderId: string,
): Promise<AxiosResponse<ApiResponse<DocumentUploadResponse[]>>> => {
  return axiosRequest().get(`${resource}/${orderId}/documents`);
};

export const addRetention = async (
  id: string,
  data: {
    percent: number;
    dueDate?: string;
    assigneeId?: string;
  }[],
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().post(`${resource}/${id}/retentions`, { version, retentions: data });
};

export const editRetention = async (
  orderId: string,
  retentionId: string,
  data: {
    percent: number;
    // `null` clears the field; `undefined` would be dropped from the JSON body and read as
    // "leave it alone", which is not a thing a clearable control can mean.
    dueDate?: string | null;
    assigneeId?: string | null;
  },
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().patch(`${resource}/${orderId}/retentions/${retentionId}`, {
    ...data,
    version,
  });
};

export const deleteRetention = async (
  orderId: string,
  retentionId: string,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().delete(`${resource}/${orderId}/retentions/${retentionId}`);
};

export const addValueContraCharge = async (
  id: string,
  data: {
    amount: number;
  }[],
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().post(`${resource}/${id}/contra-charges/value`, {
    version,
    valueContraCharges: data,
  });
};

export const editValueContraCharge = async (
  orderId: string,
  contraId: string,
  data: {
    amount: number;
  },
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().patch(`${resource}/${orderId}/contra-charges/value/${contraId}`, {
    ...data,
    version,
  });
};

export const deleteValueContraCharge = async (
  orderId: string,
  contraId: string,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().delete(`${resource}/${orderId}/contra-charges/value/${contraId}`);
};

export const addItemContraCharge = async (
  id: string,
  data: ItemContraChargeRequest[],
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().post(`${resource}/${id}/contra-charges/item`, {
    version,
    itemContraCharges: data,
  });
};

export const editItemContraCharge = async (
  orderId: string,
  contraId: string,
  data: ItemContraChargeRequest,
  version: number,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().patch(`${resource}/${orderId}/contra-charges/item/${contraId}`, {
    ...data,
    version,
  });
};

export const deleteItemContraCharge = async (
  orderId: string,
  contraId: string,
): Promise<AxiosResponse<ApiResponse<OrderClaim>>> => {
  return axiosRequest().delete(`${resource}/${orderId}/contra-charges/item/${contraId}`);
};

export const getOrderComplianceInfo = async (
  orderId: string,
): Promise<AxiosResponse<ApiResponse<ComplianceInfo>>> => {
  return axiosRequest().get(`${resource}/${orderId}/compliance`);
};
